import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { Shell } from '../components/Shell'
import { Button, Sill, ToggleRow } from '../components/ui'

/**
 * Whether a church can rely on its own CCLI/APRA licence for a song, or needs
 * Beseter to be the record of direct permission, turns on one fact: is the
 * writer an APRA AMCOS member. Asked once at signup, and re-asked as a gate
 * before uploading for anyone who joined before this existed — a song can't
 * go out without it on record.
 */
export default function RightsInfo() {
  const { user, refresh } = useSession()
  const nav = useNavigate()
  const location = useLocation()
  const dest = location.state?.from || '/home'

  const [member, setMember] = useState(null) // true | false | null (unanswered)
  const [consent, setConsent] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      const { error } = await supabase.from('users').update({
        apra_amcos_member: member,
        direct_licence_opt_out: member === false ? !consent : false,
        rights_info_confirmed_at: new Date().toISOString(),
      }).eq('id', user.id)
      if (error) throw error
      await refresh()
      nav(dest, { replace: true })
    } catch (e) { setErr(readableError(e)) } finally { setBusy(false) }
  }

  return (
    <Shell>
      <div className="eyebrow">Rights & licensing</div>
      <h2 className="q">Are you an APRA AMCOS member?</h2>
      <p className="sub">
        This decides whether a church's own CCLI licence already covers your songs,
        or whether Beseter is the record of your permission for them to use one.
      </p>
      <Sill />

      <div className="lanes">
        <button type="button" className={`lane${member === true ? ' picked' : ''}`}
                onClick={() => setMember(true)}>
          <span className="lane-t">Yes, I'm a member
            <small>My songs are already covered by a church's CCLI licence</small>
          </span>
        </button>
        <button type="button" className={`lane${member === false ? ' picked' : ''}`}
                onClick={() => setMember(false)}>
          <span className="lane-t">No, I'm not a member
            <small>A church needs my direct permission to use my songs</small>
          </span>
        </button>
      </div>

      {member === false && (
        <div style={{ marginTop: 18 }}>
          <ToggleRow
            title="Let churches use your songs under Beseter's direct licence"
            hint="On by default. Turn this off and churches will need to ask you directly before using a song."
            on={consent}
            onChange={setConsent}
          />
        </div>
      )}

      <Button busy={busy} disabled={member === null} onClick={save} style={{ marginTop: 18 }}>
        Continue
      </Button>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
